import React from "react";
import OrderTable from "./OrderTable";
import PaginationComponent from "../../../utils/TablePagination";
import LoadingScreen from "../../../utils/LoadingScreen";

const normalizeStatus = (status) => {
  return status?.toLowerCase()?.replace("-", "_")?.trim();
};

const statusConfig = {
  completed: {
    label: "Completed",
    bg: "rgba(242,252,248,1)",
    color: "rgba(1,195,125,1)",
  },
  delivered: {
    label: "Delivered",
    bg: "rgba(242,252,248,1)",
    color: "rgba(1,195,125,1)",
  },
  dispatched: { label: "Dispatched", bg: "#E8E0FF", color: "#4A00B3" },
  ready_for_takeaway: {
    label: "Ready Takeaway",
    bg: "rgba(242,252,248,1)",
    color: "rgba(1,195,125,1)",
  },
  ready_for_dinning: {
    label: "Ready Dining",
    bg: "rgba(242,252,248,1)",
    color: "rgba(1,195,125,1)",
  },
  rejected: {
    label: "Rejected",
    bg: "rgba(255,224,224,1)",
    color: "rgba(255,0,0,1)",
  },
  refunded: {
    label: "Refunded",
    bg: "rgba(224,255,224,1)",
    color: "rgba(0,128,0,1)",
  },
  driver_assigned: {
    label: "Driver Assigned",
    bg: "#E0EFFF",
    color: "#0056b3",
  },
  driver_confirmed: {
    label: "Driver Confirmed",
    bg: "#CCE7D0",
    color: "#2F6D32",
  },
  delivery_picked: {
    label: "Delivery Picked",
    bg: "#FFE0E0",
    color: "#B30000",
  },
  driver_pickup_arrival: {
    label: "Pickup Arrival",
    bg: "#FFF3CD",
    color: "#856404",
  },
  driver_dropoff_arrival: {
    label: "Drop Arrival",
    bg: "#D1ECF1",
    color: "#0C5460",
  },
  not_delivered: {
    label: "Not Delivered",
    bg: "rgba(255,224,224,1)",
    color: "rgba(255,0,0,1)",
  },
};

const getStatusBadge = (rawStatus) => {
  const status = normalizeStatus(rawStatus);

  const config = statusConfig[status] || {
    label: rawStatus,
    bg: "#f1f3f5",
    color: "#495057",
  };

  return (
    <span
      className="text-xs px-2 py-1 rounded-full font-semibold"
      style={{ background: config.bg, color: config.color }}
    >
      {config.label}
    </span>
  );
};

const getOrderTypeBadge = (order) => {
  const type = (order.orderType || order.order_type || "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");

  const config = {
    home_delivery: {
      label: "Home Delivery",
      className: "bg-green-100 text-green-700 border border-green-200",
    },
    takeaway: {
      label: "Takeaway",
      className: "bg-yellow-100 text-yellow-700 border border-yellow-200",
    },
    dining: {
      label: "Dining",
      className: "bg-purple-100 text-purple-700 border border-purple-200",
    },
    car_order: {
      label: "Car Order",
      className: "bg-blue-100 text-blue-700 border border-blue-200",
    },
    office_order: {
      label: "Office Order",
      className: "bg-orange-100 text-orange-700 border border-orange-200",
    },
  };

  const item = config[type] || {
    label: type ? type.replace(/_/g, " ") : "N/A",
    className: "bg-gray-100 text-gray-700 border border-gray-200",
  };

  return (
    <span
      className={`inline-flex items-center justify-center w-[130px] py-2 text-sm font-semibold rounded-lg capitalize ${item.className}`}
    >
      {item.label}
    </span>
  );
};

const getFinalStatus = (order) => {
  const status = normalizeStatus(order.status);

  if (
    status === "ready_for_takeaway" ||
    status === "ready_for_dinning" ||
    status === "delivered"
  ) {
    return "completed";
  }

  return status;
};

const OrderHistoryTable = ({
  orderHistory,
  handleRowClickHistory,
  openRowHistory,
  orderDetails,
  selectedDeliveryPerson,
  KitchenPrint,
  reciptDetails,
  handleMarkDelivered,
  loadingOrders,
  handlePrintClick,
  handleImageClick,
}) => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 15;

  const totalPages = Math.ceil(orderHistory.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const paginatedOrders = orderHistory.slice(startIndex, endIndex);

  return (
    <>
      <table className="w-full text-sm montserrat-font-1">
        <thead className="sticky top-0 z-10 bg-green-100 montserrat-font-1">
          <tr>
            <th className="p-4 text-start">Daily OR No</th>
            <th className="p-4 text-start pl-0">Order ID</th>
            <th className="p-4 text-start pl-0">Date</th>
            <th className="p-4 text-start pl-0">Customer</th>
            <th className="p-4 text-start pl-0">Order Type</th>
            <th className="p-4 text-start pl-0">Payment</th>
            <th className="p-4 text-start pl-0">Status</th>
            <th className="p-4 text-start pl-0">Print</th>
          </tr>
        </thead>

        <tbody>
          {loadingOrders ? (
            <tr>
              <td colSpan="8" className="text-center py-10">
                <LoadingScreen text="Loading orders..." />
              </td>
            </tr>
          ) : paginatedOrders.length === 0 ? (
            <tr>
              <td colSpan="8" className="text-center py-6 text-gray-500">
                No Order History Found
              </td>
            </tr>
          ) : (
            paginatedOrders.map((order, index) => (
              <React.Fragment key={order.orderid}>
                <tr
                  className="border-t cursor-pointer hover:bg-gray-50"
                  onClick={() => handleRowClickHistory(index, order.orderid)}
                >
                  <td className="p-8">
                    <strong>{order.daily_order_id || "N/A"}</strong>
                  </td>

                  <td className="text-start">
                    <div className="flex flex-col items-start gap-1">
                      {/* Order ID */}
                      <div>
                        <span>ORD ID: {order.orderid}</span>
                      </div>

                      {/* SR */}
                      {order.srNo && (
                        <span className="inline-flex bg-green-100 text-blue-600 px-3 py-1 rounded-full text-xs font-semibold w-fit">
                          SR: {order.srNo}
                        </span>
                      )}
                    </div>
                  </td>

                  <td>
                    <div>{order.order_date?.split(" ")[0]}</div>
                    <div className="text-xs text-gray-500">
                      {order.order_date?.split(" ")[1]}
                    </div>
                  </td>

                  <td>
                    <div>{order.customer_name}</div>
                    <div className="text-xs text-gray-500">
                      {order.customer_mobile}
                    </div>
                  </td>

                  <td>
                    {(() => {
                      const type = (order.orderType || order.order_type || "")
                        .toLowerCase()
                        .trim()
                        .replace(/\s+/g, "_");

                      const config = {
                        home_delivery: {
                          label: "Home Delivery",
                          className:
                            "bg-green-100 text-green-700 border border-green-200",
                        },
                        takeaway: {
                          label: "Takeaway",
                          className:
                            "bg-yellow-100 text-yellow-700 border border-yellow-200",
                        },
                        dining: {
                          label: "Dining",
                          className:
                            "bg-purple-100 text-purple-700 border border-purple-200",
                        },
                        car_order: {
                          label: "Car Order",
                          className:
                            "bg-blue-100 text-blue-700 border border-blue-200",
                        },
                        office_order: {
                          label: "Office Order",
                          className:
                            "bg-orange-100 text-orange-700 border border-orange-200",
                        },
                      };

                      const item = config[type] || {
                        label: type ? type.replace(/_/g, " ") : "N/A",
                        className:
                          "bg-gray-100 text-gray-700 border border-gray-200",
                      };

                      return (
                        <span
                          className={`inline-flex items-center justify-center w-[130px] py-2 text-sm font-semibold rounded-lg capitalize ${item.className}`}
                        >
                          {item.label}
                        </span>
                      );
                    })()}
                  </td>

                  <td>{order.payment_mode}</td>

                  <td className="px-3 py-2">
                    {getStatusBadge(getFinalStatus(order))}
                  </td>

                  <td>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrintClick(order);
                      }}
                      className="px-4 py-2 bg-blue-500 rounded text-white"
                    >
                      Print
                    </button>
                  </td>
                </tr>

                {openRowHistory === index && (
                  <tr className="bg-gray-100">
                    <td colSpan="8">
                      <OrderTable
                        orderDetails={orderDetails}
                        selectedDeliveryPerson={selectedDeliveryPerson}
                        activeTab="history"
                        handleKitchenPrint={KitchenPrint}
                        reciptDetails={reciptDetails}
                        handleMarkDelivered={handleMarkDelivered}
                        handleImageClick={handleImageClick}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          )}
        </tbody>
      </table>

      <PaginationComponent
        currentPage={currentPage}
        totalPages={totalPages}
        onNextPage={() =>
          setCurrentPage((prev) => Math.min(prev + 1, totalPages))
        }
        onPrevPage={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        onPageClick={(page) => setCurrentPage(page)}
      />
    </>
  );
};

export default OrderHistoryTable;
